import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Building2,
  Users,
  CheckCircle2,
  Clock,
  Percent,
  Award,
  AlertTriangle,
  Calendar,
  RefreshCw,
  Search,
  Phone,
  Mail,
  BookOpen,
  UserCheck
} from 'lucide-react';
import { AnalyticsChartsGallery } from '../components/AnalyticsChartsGallery';
import { MarksEntryStatusModule } from '../components/MarksEntryStatusModule';
import { StudentAcademicHistoryModal } from '../components/StudentAcademicHistoryModal';

export const BEODashboardPage: React.FC<{ onNavigate?: (tab: string) => void }> = ({ onNavigate }) => {
  const { user, fetchWithAuth } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState('August');
  const [beoData, setBeoData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`/api/beo/dashboard-kpis?month=${selectedMonth}`);
      const json = await res.json();
      if (json.success) {
        setBeoData(json.data);
      }
    } catch (e) {
      console.error('Failed to load BEO data:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedMonth]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'APPROVED':
      case 'VERIFIED':
        return <span className="bg-emerald-100 text-emerald-800 font-semibold px-2 py-0.5 rounded text-[11px] border border-emerald-300">Verified</span>;
      case 'SUBMITTED':
        return <span className="bg-blue-100 text-blue-800 font-semibold px-2 py-0.5 rounded text-[11px] border border-blue-300">Submitted</span>;
      case 'DRAFT':
        return <span className="bg-amber-100 text-amber-800 font-semibold px-2 py-0.5 rounded text-[11px] border border-amber-300">Draft</span>;
      case 'REVISION_REQUIRED':
        return <span className="bg-rose-100 text-rose-800 font-semibold px-2 py-0.5 rounded text-[11px] border border-rose-300">Returned</span>;
      default:
        return <span className="bg-slate-100 text-slate-700 font-medium px-2 py-0.5 rounded text-[11px] border border-slate-300">Not Started</span>;
    }
  };

  const filteredSchools = Array.isArray(beoData?.schools_list)
    ? beoData.schools_list.filter((s: any) =>
        (s?.school_name?.toLowerCase() ?? '').includes(search.toLowerCase()) ||
        (s?.school_code?.toLowerCase() ?? '').includes(search.toLowerCase())
      )
    : [];

  return (
    <div className="space-y-6 pb-12">
      {/* Header Bar */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="text-xs font-bold text-blue-700 uppercase tracking-wider">
            Block Education Officer (BEO) Monitoring Portal
          </div>
          <h1 className="text-lg md:text-xl font-bold text-[#0F2C59]">
            Block {beoData?.block || user?.block || 'Nuh'} — Monthly Assessment & SAT Progress
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => onNavigate && onNavigate('daily-attendance')}
            className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded text-xs font-bold transition flex items-center space-x-1.5 shadow-2xs"
          >
            <UserCheck className="w-3.5 h-3.5" />
            <span>Daily Attendance (6–12)</span>
          </button>

          <div className="flex items-center space-x-1.5 bg-slate-50 border border-slate-300 px-2.5 py-1.5 rounded text-xs">
            <Calendar className="w-3.5 h-3.5 text-slate-500" />
            <span className="font-semibold text-slate-700">Month:</span>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="bg-transparent font-bold text-[#0F2C59] focus:outline-none"
            >
              <option value="April">April</option>
              <option value="May">May</option>
              <option value="July">July</option>
              <option value="August">August</option>
              <option value="September">September</option>
              <option value="October">October</option>
            </select>
          </div>

          <button
            onClick={loadData}
            className="p-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded text-slate-700 transition"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Block KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <div className="bg-white p-3.5 rounded-lg border border-slate-200 shadow-xs">
          <div className="text-slate-500 text-[10px] font-bold uppercase">Schools in Block</div>
          <div className="text-xl font-bold text-slate-900 mt-1">{beoData?.total_schools ?? 0}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Government Schools</div>
        </div>

        <div className="bg-white p-3.5 rounded-lg border border-slate-200 shadow-xs">
          <div className="text-slate-500 text-[10px] font-bold uppercase">Active Students</div>
          <div className="text-xl font-bold text-slate-900 mt-1">{(beoData?.current_active_students || beoData?.total_students || 0).toLocaleString('en-IN')}</div>
          <div className="text-[10px] text-emerald-600 font-semibold mt-0.5">Enrolled: {(beoData?.total_enrolled_students || 0).toLocaleString('en-IN')}</div>
        </div>

        <div className="bg-white p-3.5 rounded-lg border border-slate-200 shadow-xs">
          <div className="text-emerald-700 text-[10px] font-bold uppercase">Submitted</div>
          <div className="text-xl font-bold text-emerald-700 mt-1">{beoData?.submitted_schools ?? 0}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">For {selectedMonth}</div>
        </div>

        <div className="bg-white p-3.5 rounded-lg border border-slate-200 shadow-xs">
          <div className="text-amber-700 text-[10px] font-bold uppercase">Pending</div>
          <div className="text-xl font-bold text-amber-700 mt-1">{beoData?.pending_schools ?? 0}</div>
          <div className="text-[10px] text-amber-600 font-semibold mt-0.5">Needs Followup</div>
        </div>

        <div className="bg-white p-3.5 rounded-lg border border-slate-200 shadow-xs">
          <div className="text-blue-700 text-[10px] font-bold uppercase">Completion</div>
          <div className="text-xl font-bold text-[#0F2C59] mt-1">{beoData?.completion_percentage ?? 0}%</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Block Compliance</div>
        </div>

        <div className="bg-white p-3.5 rounded-lg border border-slate-200 shadow-xs">
          <div className="text-indigo-700 text-[10px] font-bold uppercase">Average Score</div>
          <div className="text-xl font-bold text-indigo-900 mt-1">{beoData?.average_score !== undefined ? `${beoData.average_score}%` : '0.0%'}</div>
          <div className="text-[10px] text-slate-500 font-medium mt-0.5">From Active Marks</div>
        </div>

        <div className="bg-white p-3.5 rounded-lg border border-rose-200 shadow-xs bg-rose-50/30">
          <div className="text-rose-700 text-[10px] font-bold uppercase">Low Perf. (&lt;40%)</div>
          <div className="text-xl font-bold text-rose-700 mt-1">{beoData?.low_performing_schools ?? 0}</div>
          <div className="text-[10px] text-rose-600 font-semibold mt-0.5">Remedial Focus</div>
        </div>
      </div>

      {/* Block Schools Status Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex justify-between items-center">
          <div>
            <h2 className="text-sm font-bold text-[#0F2C59]">
              Block Schools Status & Submission Progress ({beoData?.block || user?.block || 'Nuh'} Block)
            </h2>
            <div className="text-xs text-slate-500">
              Real-time monitoring of all government schools assigned to this block
            </div>
          </div>

          <div className="relative w-64">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search school in block..."
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-300 rounded bg-slate-50"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-100 text-slate-700 uppercase font-bold text-[11px] border-b border-slate-200">
              <tr>
                <th className="py-2.5 px-3.5">School Code</th>
                <th className="py-2.5 px-3.5">School Name</th>
                <th className="py-2.5 px-3">Category</th>
                <th className="py-2.5 px-3 text-right">Students</th>
                <th className="py-2.5 px-3 text-center">Submission Status</th>
                <th className="py-2.5 px-3.5 text-right">Average Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredSchools.map((s: any) => (
                <tr key={s.id} className="hover:bg-slate-50 transition font-medium">
                  <td className="py-2.5 px-3.5 font-bold text-slate-900">{s.school_code}</td>
                  <td className="py-2.5 px-3.5 font-bold text-[#0F2C59]">{s.school_name}</td>
                  <td className="py-2.5 px-3 text-slate-600">{s.category}</td>
                  <td className="py-2.5 px-3 text-right font-semibold text-slate-900">{(s.total_students || 0).toLocaleString('en-IN')}</td>
                  <td className="py-2.5 px-3 text-center">{getStatusBadge(s.status)}</td>
                  <td className="py-2.5 px-3.5 text-right font-extrabold text-slate-900">{s.average_score}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---------------------------------------------------- */}
      {/* MULTI-DIMENSIONAL VISUAL ANALYTICS (15 CHARTS)       */}
      {/* ---------------------------------------------------- */}
      <AnalyticsChartsGallery
        selectedMonth={selectedMonth}
        selectedBlock={beoData?.block || user?.block || 'Taoru'}
      />

      {/* ---------------------------------------------------- */}
      {/* BLOCK ASSESSMENT ENTRY MONITORING & MATRIX           */}
      {/* ---------------------------------------------------- */}
      <MarksEntryStatusModule
        defaultMonth={selectedMonth}
        defaultBlock={beoData?.block || user?.block || 'Taoru'}
        title={`Block ${beoData?.block || user?.block || ''} Assessment Entry Monitoring & Subject Matrix`}
        onOpenStudent={(stId) => setSelectedStudentId(stId)}
      />

      {/* Student Academic History Dossier Modal */}
      {selectedStudentId && (
        <StudentAcademicHistoryModal
          studentId={selectedStudentId}
          onClose={() => setSelectedStudentId(null)}
        />
      )}
    </div>
  );
};
